import React, { useEffect } from 'react';
import {
    DiscordIcon,
    FooterWrapper,
    SocialButton,
    TelegramIcon,
    TwitterIcon,
    YoutubeIcon,
} from './HyperTrackerFooterStyled';

import { useTranslation } from 'react-i18next';
import { Box, Divider, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { externalUrls, ROUTES } from '@/lib/constants';
import { useDispatch, useSelector } from 'react-redux';
import { selectFavoriteCoinsList } from '@/state/hypertracker/hypertrackerSelector';
import { useNavigate } from 'react-router';
import { deleteCoinFromFavorites, getFavoriteCoins } from '@/api/hypertrackerApi';
import { setFavoriteCoinsList } from '@/state/hypertracker/hypertackerSlice';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';

const HyperTrackerFooter: React.FC<{ isMobileVisible?: boolean; isFixed?: boolean }> = ({
    isMobileVisible = true,
    isFixed = true,
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'footer' });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const favoriteCoins = useSelector(selectFavoriteCoinsList);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isMemberAuthorized = useIsMemberAuthorized();

    useEffect(() => {
        if (!isMemberAuthorized || favoriteCoins?.length) return;
        getFavoriteCoins()
            .then((response) => dispatch(setFavoriteCoinsList(response.data)))
            .catch(console.error);
    }, [isMemberAuthorized]);

    const handleRemoveFavorite = async (e: React.MouseEvent, coin: string) => {
        e.stopPropagation();
        try {
            await deleteCoinFromFavorites({ coin, type: 'perp' });
            const { data } = await getFavoriteCoins();
            dispatch(setFavoriteCoinsList(data));
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <FooterWrapper
            data-testid="footer"
            className={!isMobileVisible ? 'desktop' : ''}
            sx={{
                ...(isFixed && {
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                }),
            }}
        >
            {!isMobile && favoriteCoins.length > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '94px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <Typography
                        variant={'p12'}
                        fontWeight={600}
                        color={theme.palette.primitives.woodsmoke[500]}
                    >
                        {t('fav')}
                    </Typography>
                    {favoriteCoins.map((fc) => (
                        <Box
                            key={fc.coin}
                            onClick={() => navigate(`${ROUTES.HYPERTRACKER_PERPS}/${fc.coin}`)}
                            className="fav-coin-block"
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                background: theme.palette.primitives.woodsmoke[900],
                                cursor: 'pointer',
                                '&:hover': {
                                    background: '#1C312D',
                                },
                                '&:hover .fav-coin-label': {
                                    color: theme.palette.hypertracker.light,
                                },
                            }}
                        >
                            <Typography
                                variant={'p12'}
                                fontWeight={400}
                                className="fav-coin-label"
                                color={theme.palette.primitives.woodsmoke[600]}
                            >
                                {fc.coin}
                            </Typography>
                            <Tooltip title={t('remove_fav')} placement="top">
                                <CloseIcon
                                    onClick={(e) => handleRemoveFavorite(e, fc.coin)}
                                    sx={{
                                        fontSize: '12px',
                                        color: theme.palette.primitives.woodsmoke[600],
                                        cursor: 'pointer',
                                    }}
                                />
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
            )}
            {!isMobile ? (
                <>
                    <SocialButton
                        data-testid="hypertracker-footer-api-link"
                        href={ROUTES.HYPERTRACKER_API}
                        target={'_blank'}
                        rel={'noreferrer'}
                        color={'secondary.main'}
                        sx={{
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        <Typography variant={isMobile ? 'p14' : 'p12'} fontWeight={400}>
                            {t('api')}
                        </Typography>
                    </SocialButton>
                    <Divider orientation="vertical" flexItem />
                </>
            ) : (
                ''
            )}
            <SocialButton
                data-testid="hypertracker-footer-about-link"
                href={externalUrls.hypertrackerDashboard}
                target={'_blank'}
                rel={'noreferrer'}
                color={'text.primary'}
                sx={{
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                <Typography variant={'p12'} fontWeight={500}>
                    {t('about')}
                </Typography>
            </SocialButton>

            <Divider orientation="vertical" flexItem />

            <SocialButton
                data-testid="hypertracker-footer-x-link"
                href={'https://x.com/HyperTracker'}
                target={'_blank'}
                rel={'noreferrer'}
                aria-label="X"
                color={'text.primary'}
                sx={{
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                <TwitterIcon />
            </SocialButton>

            <Divider orientation="vertical" flexItem />

            <SocialButton
                data-testid="hypertracker-footer-discord-link"
                href={'https://discord.gg/szZ4X3Z'}
                target={'_blank'}
                rel={'noreferrer'}
                aria-label="Discord"
                color={'text.primary'}
                sx={{
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                <DiscordIcon />
            </SocialButton>

            <Divider orientation="vertical" flexItem />

            <SocialButton
                data-testid="hypertracker-footer-youtube-link"
                href={'https://www.youtube.com/@coinmarketman'}
                target={'_blank'}
                rel={'noreferrer'}
                aria-label="YouTube"
                color={'text.primary'}
                sx={{
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                <YoutubeIcon />
            </SocialButton>

            <Divider orientation="vertical" flexItem />

            <SocialButton
                data-testid="hypertracker-footer-telegram-link"
                href={'https://t.me/HyperTrackerio'}
                target={'_blank'}
                rel={'noreferrer'}
                aria-label="Telegram"
                color={'text.primary'}
                sx={{
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                <TelegramIcon />
            </SocialButton>
        </FooterWrapper>
    );
};

export default HyperTrackerFooter;
